import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import yn from 'yn';
import { clientbookApi } from '../../core/clientbook-api';
import { messageStatusSubject } from '../../core/events';
import { CLIENT_INBOX_PATH } from '../../components/Layout/SocketHandler/shared/constants';

export const LayoutsContext = createContext({});

const LayoutsContextProvider = ({ children, auth, inboxList, inboxListQuery }) => {
  const [newMessagesId, setNewMessagesId] = useState([]);
  const [hasUndelivered, setHasUndelivered] = useState(false);
  const hasNewMessages = useMemo(() => newMessagesId.length > 0, [newMessagesId]);

  const fetchMyChats = useCallback(
    filters => {
      const data = {
        storeId: auth.selectedStore,
        associateIds: [auth.userId],
        offset: 0,
        ...filters,
      };

      // Closed conversations still count as new messages unless the merchant has
      // Decluttered Inbox enabled, where they live in their own tab (ENG-3565).
      if (yn(auth.merchantConfig?.DECLUTTERED_INBOX)) {
        data.open = true;
      }

      return clientbookApi.post({ url: '/chats', data });
    },
    [auth],
  );

  const checkNewMessages = useCallback(() => {
    if (!auth.merchantId) return;
    try {
      fetchMyChats({ read: false, limit: 10 }).then(res => {
        if (res.data && res.data.length > 0) {
          const newMessages = res.data.filter(message => message.isUnread);
          setNewMessagesId(newMessages.map(message => message.client.id));
        } else {
          setNewMessagesId([]);
        }
      });
    } catch (error) {
      console.error('Error while fetching new messages', error);
    }
  }, [auth, fetchMyChats]);

  const checkUndelivered = useCallback(() => {
    if (!auth.merchantId) return;
    fetchMyChats({ undelivered: true, limit: 1 })
      .then(res => setHasUndelivered(res.data?.length > 0))
      .catch(error => console.error('Error while fetching undelivered messages', error));
  }, [auth, fetchMyChats]);

  const addNewMessageId = useCallback(
    id => {
      setNewMessagesId([...newMessagesId, id]);
    },
    [newMessagesId],
  );

  useEffect(() => {
    // A filtered or searched list only holds part of My Inbox, so fall back to
    // the API for whichever dot the list can't answer (ENG-3525).
    const listIsComplete = inboxList?.length > 0 && !inboxListQuery?.search;

    if (listIsComplete && inboxListQuery?.filter !== 'undelivered') {
      const newMessages = inboxList.filter(message => message.isUnread);
      setNewMessagesId(newMessages.map(message => message.client.id));
    } else {
      checkNewMessages();
    }

    if (listIsComplete && inboxListQuery?.filter !== 'unread') {
      setHasUndelivered(inboxList.some(message => message.isUnreadError));
    } else {
      checkUndelivered();
    }
  }, [inboxList, inboxListQuery, auth.merchantId]);

  // On the inbox page ChatListBar marks the row in inboxList (skipping the open
  // chat) and the effect above picks it up; elsewhere, ask the API.
  useEffect(() => {
    const subscription = messageStatusSubject.subscribe(event => {
      if (event?.status !== 'failed' && event?.status !== 'undelivered') return;
      if (window.location.pathname === CLIENT_INBOX_PATH) return;
      checkUndelivered();
    });
    return () => subscription.unsubscribe();
  }, [checkUndelivered]);

  return (
    <LayoutsContext.Provider
      value={{
        hasNewMessages,
        hasUndelivered,
        addNewMessageId,
      }}
    >
      {children}
    </LayoutsContext.Provider>
  );
};

export function useLayout() {
  const context = useContext(LayoutsContext);

  if (!context) {
    throw new Error('useLayoutsContext must be used within an LayoutsContext');
  }
  return context;
}

const mapStateToProps = state => ({
  auth: state.auth,
  inboxList: state.messaging.inboxList,
  inboxListQuery: state.messaging.inboxListQuery,
});
export default connect(mapStateToProps)(LayoutsContextProvider);
